/* Oakman - Sauna Playbook CTA
 *
 * Three jobs:
 *   1. Move the inline card into the article body, after the Nth paragraph.
 *   2. Distribute the one-line orange CTAs every N paragraphs after that,
 *      cycling through the hooks.
 *   3. Open the live Klaviyo Sauna Playbook popup on click, picking the desktop
 *      or mobile form by viewport, and report the click to GA by position.
 *
 * Fails safe. If the article body cannot be found, the card stays where Liquid
 * rendered it and the lines are removed rather than dumped in the wrong place.
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 767px)';

  function articleBody() {
    return (
      document.querySelector('.blog-post-content rte-formatter') ||
      document.querySelector('.blog-post-content') ||
      document.querySelector('.article-template__content, .rte--article, [data-article-content]')
    );
  }

  /* Direct child paragraphs with enough text to count as real prose. Short
     captions and image-only paragraphs are skipped so a CTA never lands in the
     middle of a photo sequence. Our own injected CTAs are excluded so repeated
     runs cannot compound. */
  function realParagraphs(body) {
    return Array.prototype.filter.call(body.children, function (el) {
      if (el.tagName !== 'P') return false;
      if (el.hasAttribute('data-oak-cta')) return false;
      if (el.querySelector('img, iframe, video')) return false;
      return el.textContent.trim().length > 40;
    });
  }

  function placeCard(cta, body) {
    if (!body) {
      cta.hidden = false;
      return;
    }

    /* Honour a manually pasted placeholder if an editor wants a specific spot. */
    var manual = body.querySelector('[data-oak-cta-slot]');
    if (manual && manual.parentNode) {
      manual.parentNode.replaceChild(cta, manual);
      cta.hidden = false;
      return;
    }

    var after = parseInt(cta.getAttribute('data-insert-after'), 10);
    if (isNaN(after) || after < 1) after = 1;

    var min = parseInt(cta.getAttribute('data-min-paragraphs'), 10);
    if (isNaN(min) || min < 1) min = 4;

    var paras = realParagraphs(body);

    /* On a short article, an interruption plus the end-of-article card is too
       much. Drop the card and let the end one do the work. */
    if (paras.length < min) {
      if (cta.parentNode) cta.parentNode.removeChild(cta);
      return;
    }

    /* Never insert so late that there is no article left underneath it. */
    if (after > paras.length - 1) after = paras.length - 1;

    paras[after - 1].insertAdjacentElement('afterend', cta);
    cta.hidden = false;
  }

  function isRealParagraph(el) {
    if (el.tagName !== 'P') return false;
    if (el.hasAttribute('data-oak-cta')) return false;
    if (el.querySelector('img, iframe, video')) return false;
    return el.textContent.trim().length > 40;
  }

  /* Lines sit immediately BEFORE a section heading, never between paragraphs
     inside a section, so they always land at a natural pause in the article.
     A heading only qualifies once at least `minGap` paragraphs have gone by
     since the last CTA, which keeps them from bunching up when an article has
     several short sections in a row. */
  function placeLines(template, body) {
    var hooks = Array.prototype.slice.call(template.children);
    if (!body || !hooks.length) {
      if (template.parentNode) template.parentNode.removeChild(template);
      return [];
    }

    var minGap = parseInt(template.getAttribute('data-line-min'), 10);
    if (isNaN(minGap) || minGap < 1) minGap = 5;

    var children = Array.prototype.slice.call(body.children);

    /* How many real paragraphs remain after each index, so we never place a
       line just above the end of the article where the closing card already is. */
    var remaining = new Array(children.length);
    var running = 0;
    for (var r = children.length - 1; r >= 0; r--) {
      remaining[r] = running;
      if (isRealParagraph(children[r])) running++;
    }

    var placed = [];
    var index = 0;
    var since = 0;

    for (var i = 0; i < children.length; i++) {
      var el = children[i];

      /* The card resets the count, so the first line is a full gap past it. */
      if (el.hasAttribute && el.hasAttribute('data-oak-cta')) {
        since = 0;
        continue;
      }

      if (isRealParagraph(el)) {
        since++;
        continue;
      }

      if (el.tagName === 'H2' && since >= minGap && remaining[i] >= 2) {
        var node = hooks[index % hooks.length].cloneNode(true);
        node.setAttribute('data-position', 'line-' + (index + 1));
        el.insertAdjacentElement('beforebegin', node);
        placed.push(node);
        index++;
        since = 0;
      }
    }

    if (template.parentNode) template.parentNode.removeChild(template);
    return placed;
  }

  function openForm(cta) {
    var isMobile = window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
    var id = isMobile
      ? cta.getAttribute('data-form-mobile')
      : cta.getAttribute('data-form-desktop');
    var fallback = cta.getAttribute('data-fallback-url');

    /* Report the click by position so GA can tell us which placement earns
       attention. Klaviyo credits every signup to the form itself, so this is
       the only per-position signal available. */
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'playbook_cta_click', {
        cta_variant: cta.getAttribute('data-variant') || 'card',
        cta_position: cta.getAttribute('data-position') || 'primary'
      });
    }

    /* klaviyo.js is loaded async in theme.liquid. If it has not arrived, send
       the reader somewhere useful instead of doing nothing. */
    if (!window.klaviyo && !window._klOnsite) {
      if (fallback) window.location.href = fallback;
      return;
    }

    if (!id) {
      if (fallback) window.location.href = fallback;
      return;
    }

    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', id]);
  }

  function wire(cta) {
    var btn = cta.querySelector('[data-oak-cta-open]');
    if (!btn || btn.hasAttribute('data-oak-wired')) return;
    btn.setAttribute('data-oak-wired', '');
    btn.addEventListener('click', function () {
      openForm(cta);
    });
  }

  function init() {
    var body = articleBody();

    var card = document.querySelector('[data-oak-cta][data-variant="inline"]');
    if (card) placeCard(card, body);

    var lines = [];
    var template = document.querySelector('[data-oak-cta-lines]');
    if (template) lines = placeLines(template, body);

    var all = Array.prototype.slice
      .call(document.querySelectorAll('[data-oak-cta]'))
      .concat(lines);

    all.forEach(function (cta) {
      if (cta.parentNode) wire(cta);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
